import { Component } from '@angular/core';
import { BaseHttpService } from '@core/http/base-http.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { finalize } from 'rxjs/operators';
import { EntityName, EntityStateCount } from '../../enums/entity-name.enum';
import {
  CheckEntityStateCountInterface,
  CheckOrganizationAdminState,
} from '../../interfaces/check-entity-state-count.interface';
import { VentureInterface } from '../../interfaces/venture.interface';
import { Entity } from '../../models/entity.model';

@UntilDestroy()
@Component({
  selector: 'app-entity-state-count',
  templateUrl: './entity-state-count.component.html',
  // eslint-disable-next-line @angular-eslint/prefer-standalone
  standalone: false,
})
export class EntityStateCountComponent {
  private privateEntityName = '';
  private privateEntityItems: Partial<Entity>[] = [];
  private privateBaseHttpService: BaseHttpService<Entity>;

  loadingNumberOfLikes = false;
  loadingNumberOfFollowers = false;
  loadingNumberOfComments = false;
  loadingNumberOfVentures = false;
  loadingNumberOfEvents = false;

  loadingNumberOfParticipants = false;

  // #region SYNC COUNT STATE
  protected doEntityStateCount(
    entityName: string,
    entityItems: Partial<Entity>[],
    baseHttpService: BaseHttpService<Entity>
  ): void {
    this.privateEntityName = entityName;
    this.privateEntityItems = entityItems ?? [];
    this.privateBaseHttpService = baseHttpService;

    if (entityName && entityItems) {
      this.syncEntityStateCounts();
    }
  }

  protected syncEntityStateCounts(): void {
    this.syncLikingsState();
    this.syncFollowingsState();
    this.syncCommentsState();
    this.syncVenturesState();
    this.syncEventsState();
  }

  protected syncLikingsState(): void {
    this.loadingNumberOfLikes = true;
    this.syncSpecificEntityStateCount(EntityStateCount.Liking);
  }

  protected syncFollowingsState(): void {
    this.loadingNumberOfFollowers = true;
    this.syncSpecificEntityStateCount(EntityStateCount.Following);
  }

  protected syncCommentsState(): void {
    this.loadingNumberOfComments = true;
    this.syncSpecificEntityStateCount(EntityStateCount.Comment);
  }

  protected syncVenturesState(): void {
    this.loadingNumberOfVentures = true;
    this.syncSpecificEntityStateCount(EntityStateCount.Venture);
  }

  protected syncEventsState(): void {
    this.loadingNumberOfEvents = true;
    this.syncSpecificEntityStateCount(EntityStateCount.Event);
  }

  protected syncParticipantsState(): void {
    this.loadingNumberOfParticipants = true;
    this.syncSpecificEntityStateCount(EntityStateCount.Participant);
  }

  protected syncSpecificEntityStateCount(
    entityStateCount: EntityStateCount,
    directEntityName = '',
    directEntityItems: Partial<Entity>[] = [],
    directHttpService: BaseHttpService<Entity> = null
  ): void {
    const httpService = directHttpService ?? this.privateBaseHttpService;
    const entityName = directEntityName || this.privateEntityName;
    const entityItems =
      directEntityItems?.length > 0
        ? directEntityItems
        : this.privateEntityItems;

    httpService
      .checkEntityStateCount(
        entityName,
        entityItems.map((item: Entity) => item.id),
        entityStateCount
      )
      .pipe(
        untilDestroyed(this),
        finalize(() => {
          switch (entityStateCount) {
            case EntityStateCount.Liking:
              this.loadingNumberOfLikes = false;
              break;

            case EntityStateCount.Following:
              this.loadingNumberOfFollowers = false;
              break;

            case EntityStateCount.Comment:
              this.loadingNumberOfComments = false;
              break;

            case EntityStateCount.Venture:
              this.loadingNumberOfVentures = false;
              break;

            case EntityStateCount.Event:
              this.loadingNumberOfEvents = false;
              break;

            case EntityStateCount.Participant:
              this.loadingNumberOfParticipants = false;
              break;
          }
        })
      )
      .subscribe((stateCounts: CheckEntityStateCountInterface[]) => {
        this.updateEntityStateCounts(
          entityStateCount,
          entityItems,
          stateCounts
        );
      });
  }

  protected updateEntityStateCounts(
    entityStateCount: string,
    entityItems: Partial<Entity>[] = [],
    stateCounts: CheckEntityStateCountInterface[] = []
  ): void {
    for (const item of entityItems) {
      const checkItem = stateCounts.find(x => x.entityId === item.id);
      if (checkItem) {
        this.mapStateCount(item as Entity, checkItem, entityStateCount);
      }
    }
  }

  protected mapStateCount(
    item: Entity,
    checkItem: CheckEntityStateCountInterface,
    entityStateCount: string
  ): void {
    if (checkItem) {
      switch (entityStateCount) {
        case EntityStateCount.Liking:
          item.isLiked = checkItem.isFavorite;
          item.likesCount = checkItem.count;
          item.likeId = checkItem.favoriteId;
          break;

        case EntityStateCount.Following:
          item.followerCount = checkItem.count;
          item.isFavorite = checkItem.isFavorite;
          break;

        case EntityStateCount.Comment:
          item.commentsCount = checkItem.count;
          break;

        case EntityStateCount.Venture:
          item.venturesCount = checkItem.count;
          break;

        case EntityStateCount.Event:
          item.eventsCount = checkItem.count;
          break;

        case EntityStateCount.Participant:
          item.participantsCount = checkItem.count;
          break;
      }
    }
  }
  // #endregion End SYNC COUNT STATE

  protected updateEntityItemsAfterVerifyingOrgAdminRole(): void {
    if (
      this.privateEntityItems &&
      this.privateEntityName === EntityName.Venture
    ) {
      const ids = this.privateEntityItems.map((entity: Entity) => entity.id);

      this.privateBaseHttpService
        .checkUserIsOrgAdminOfEntity(ids)
        .pipe(untilDestroyed(this))
        .subscribe((lstCheckIsOrgAdminRes: CheckOrganizationAdminState[]) => {
          this.privateEntityItems = this.privateEntityItems.map(
            (entity: VentureInterface) => {
              const result = lstCheckIsOrgAdminRes.find(
                check => check.entityId === entity.id
              );
              entity.isOrganizationAdmin = result ? result.isAdmin : false;
              return entity;
            }
          );
        });
    }
  }
}
